import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Grid,
  Card,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
  Chip,
  Alert,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import CollapsibleCard from "./CollapsibleCard";
import TimePeriodSelector from "../../common/TimePeriodSelector";
import { fmtDuration } from "./ChargerUptimeV2";
import { queryKeys } from "src/react-query/queryKeys";
import { fetchWithError } from "src/react-query/fetchWithError";
import { fUTCToLocal } from "src/utils/formatTime";
import { fEnergy } from "src/utils/formatNumber";
import { hideColumnsOnPhone } from "./tableSx";
import VehicleReadinessPanel from "./VehicleReadinessPanel";
import { VehicleLink, ChargerLink } from "./DetailLinks";

// Fleet readiness, vehicles charging now and vehicle charging history. Sessions are
// matched to vehicles by the battery MAC the charger reports (each battery charges
// in its own session, so a truck can appear twice in "charging now"). Everything
// can be narrowed to one charging site (depot).

const PERIOD_OPTIONS = [
  { value: "Today", label: "Today" },
  { value: "Last7Days", label: "Last 7 Days" },
  { value: "Last30Days", label: "Last 30 Days" },
  { value: "Last90Days", label: "Last 90 Days" },
];
const PERIOD_DAYS = { Last7Days: 7, Last30Days: 30, Last90Days: 90 };
const ACTIVE_REFRESH_MS = 30000;
// 5 rows per page keeps the card compact on the dashboard.
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

const Tile = styled(Card, {
  shouldForwardProp: (prop) => prop !== "accent",
})(({ theme, accent }) => ({
  padding: theme.spacing(1.75),
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${theme.palette.grey[200]}`,
  borderTop: `3px solid ${accent}`,
  boxShadow: theme.customShadows.z1,
  height: "100%",
}));

// Period -> UTC ISO bounds, on local (browser) day boundaries.
const periodRange = (period) => {
  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (period !== "Today") start.setDate(start.getDate() - ((PERIOD_DAYS[period] || 7) - 1));
  return { fromTs: start.toISOString(), toTs: now.toISOString() };
};

// API timestamps are UTC ("2026-10-04 10:15:00"); show local.
const fTime = (utc, pattern = "dd MMM, HH:mm") => {
  const d = fUTCToLocal(utc);
  return d ? format(d, pattern) : "—";
};
const fKwh = (v) => (v === null || v === undefined ? "—" : fEnergy(v));
const fSoc = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(0)}%`);

const VehicleCell = ({ row }) =>
  // Charging Now lists every session on an In Use charger; one whose battery MAC
  // isn't registered to a vehicle (or wasn't reported) has no vehicle.
  row.vehicleId == null ? (
    <>
      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
        Unknown vehicle
      </Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {row.reportedMac ? `MAC ${row.reportedMac} · not registered to a vehicle` : "No battery MAC reported"}
      </Typography>
    </>
  ) : (
    <>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        <VehicleLink vehicleId={row.vehicleId}>{row.registrationNumber || `Vehicle ${row.vehicleId}`}</VehicleLink>
      </Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {[row.make, row.model].filter(Boolean).join(" ")}
        {row.batterySlot ? ` · Battery ${row.batterySlot}` : ""}
      </Typography>
    </>
  );
VehicleCell.propTypes = { row: PropTypes.object.isRequired };

const SocCell = ({ from, to }) => {
  const theme = useTheme();
  if ((from === null || from === undefined) && (to === null || to === undefined)) return <>—</>;
  const pct = Math.min(100, Math.max(0, Number(to ?? from) || 0));
  return (
    <Stack spacing={0.5} sx={{ minWidth: 110 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {fSoc(from)} → {fSoc(to)}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 5,
          borderRadius: 3,
          "& .MuiLinearProgress-bar": {
            backgroundColor: pct >= 80 ? theme.palette.success.main : pct >= 30 ? theme.palette.warning.main : theme.palette.error.main,
          },
        }}
      />
    </Stack>
  );
};
// mysql2 returns DECIMAL columns as strings.
const numeric = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);
SocCell.propTypes = { from: numeric, to: numeric };

const ChargerCell = ({ row }) => (
  <>
    <Typography variant="body2">
      <ChargerLink chargerPk={row.chargerPk}>{row.chargerId}</ChargerLink>
    </Typography>
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {row.chargerName && row.chargerName !== row.chargerId ? `${row.chargerName} · ` : ""}Connector {row.connectorId}
    </Typography>
  </>
);
ChargerCell.propTypes = { row: PropTypes.object.isRequired };

// "Charging Now" tab body. The parent fetches the data, so the tab label shows the
// live count (and keeps refreshing) even while the History tab is open.
const ChargingNowPanel = ({ rows, isLoading, error }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  // Clamp: the live list can shrink between refreshes while on a later page.
  const safePage = Math.min(page, Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1));
  const visible = rows.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage);
  return (
  <>
      {error ? (
        <Alert severity="error">Couldn&apos;t load vehicles charging now: {error.message}</Alert>
      ) : (
        <TableContainer>
          <Table size="small" sx={hideColumnsOnPhone([2, 3])}>
            <TableHead>
              <TableRow>
                <TableCell>Vehicle</TableCell>
                <TableCell>Charger</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell align="right">Energy</TableCell>
                <TableCell>SoC (start → now)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    Nothing is charging right now.
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                visible.map((r) => (
                  <TableRow key={r.transactionId} hover>
                    <TableCell><VehicleCell row={r} /></TableCell>
                    <TableCell><ChargerCell row={r} /></TableCell>
                    <TableCell>{fTime(r.startTime)}</TableCell>
                    <TableCell>
                      <Chip size="small" color="success" variant="outlined" label={fmtDuration(r.durationSeconds)} />
                    </TableCell>
                    <TableCell align="right">{fKwh(r.energyKwh)}</TableCell>
                    <TableCell><SocCell from={r.startSoC} to={r.currentSoC} /></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!error && !isLoading && rows.length > rowsPerPage && (
        <TablePagination
          component="div"
          count={rows.length}
          page={safePage}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      )}
  </>
  );
};
ChargingNowPanel.propTypes = {
  rows: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
};

// "History" tab body. The period is chosen in the card header (parent).
const HistoryPanel = ({ teamId, period, siteId = null }) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  // Range fixed per period selection so the query key stays stable between renders.
  // The parent keys this panel by period, so page state starts at 0 for each period.
  const range = useMemo(() => periodRange(period), [period]);

  const { data, isLoading, isFetching, error } = useQuery(
    queryKeys.vehicleChargingHistory(teamId, {
      ...range,
      ...(siteId ? { siteId } : {}),
      page: page + 1,
      limit: rowsPerPage,
    }),
    fetchWithError,
    { enabled: Boolean(teamId), keepPreviousData: true, select: (r) => r?.data ?? null }
  );
  const rows = data?.rows || [];
  const summary = data?.summary || { sessions: 0, energyKwh: 0, vehicles: 0 };
  const topVehicles = (data?.byVehicle || []).slice(0, 5);

  const tiles = [
    { key: "energy", label: "Energy Delivered", value: fKwh(summary.energyKwh), accent: theme.palette.primary.main },
    { key: "sessions", label: "Charging Sessions", value: `${summary.sessions}`, accent: theme.palette.info.main },
    { key: "vehicles", label: "Vehicles Charged", value: `${summary.vehicles}`, accent: theme.palette.success.main },
  ];

  return (
    <>
      {error ? (
        <Alert severity="error">Couldn&apos;t load vehicle charging history: {error.message}</Alert>
      ) : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {tiles.map((t, i) => (
              <Grid item xs={i === tiles.length - 1 ? 12 : 6} sm={4} key={t.key}>
                <Tile accent={t.accent}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
                    {t.label}
                  </Typography>
                  {isLoading ? (
                    <Box sx={{ py: 0.5 }}><CircularProgress size={20} /></Box>
                  ) : (
                    <Typography variant="h5" sx={{ fontWeight: 700, color: t.accent }}>{t.value}</Typography>
                  )}
                </Tile>
              </Grid>
            ))}
          </Grid>

          {topVehicles.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }} alignItems="center">
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, mr: 0.5 }}>
                Top vehicles:
              </Typography>
              {topVehicles.map((v) => (
                <Tooltip key={v.vehicleId} title={`${v.sessions} session${Number(v.sessions) === 1 ? "" : "s"}`}>
                  <Chip size="small" variant="outlined" label={`${v.registrationNumber || v.vehicleId} · ${fKwh(v.energyKwh)}`} />
                </Tooltip>
              ))}
            </Stack>
          )}

          <TableContainer sx={{ opacity: isFetching && !isLoading ? 0.6 : 1 }}>
            <Table size="small" sx={hideColumnsOnPhone([2, 4, 5])}>
              <TableHead>
                <TableRow>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Charger</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>Stop</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell align="right">Energy</TableCell>
                  <TableCell>SoC (start → stop)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={26} /></TableCell>
                  </TableRow>
                )}
                {!isLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No vehicle charging sessions in this period.
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading &&
                  rows.map((r) => (
                    <TableRow key={r.transactionId} hover>
                      <TableCell><VehicleCell row={r} /></TableCell>
                      <TableCell><ChargerCell row={r} /></TableCell>
                      <TableCell>{fTime(r.startTime)}</TableCell>
                      <TableCell>{fTime(r.stopTime)}</TableCell>
                      <TableCell>{fmtDuration(r.durationSeconds)}</TableCell>
                      <TableCell align="right">{fKwh(r.energyKwh)}</TableCell>
                      <TableCell><SocCell from={r.startSoC} to={r.stopSoC} /></TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data?.pagination?.total || 0}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </>
      )}
    </>
  );
};
HistoryPanel.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  period: PropTypes.string.isRequired,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// The Vehicle Charging section of the V2 dashboard: one card, three tabs — Readiness
// (is each truck charged enough), Charging Now (live, auto-refreshing) and History
// (period at the right of the tab row). Shown only when the company flag is on (see
// useVehicleChargingEnabled / DashboardAppV2).
// Outline inside the tab: the tab strip clips anything drawn outside it.
const TAB_SX = { minHeight: 36, textTransform: "none", fontWeight: 600, "&:focus-visible": { outlineOffset: -2 } };

const VehicleChargingV2 = ({ teamId, siteId = null }) => {
  const [tab, setTab] = useState("readiness");
  const [period, setPeriod] = useState("Last7Days");
  const siteParams = siteId ? { siteId } : undefined;

  const { data, isLoading, error } = useQuery(queryKeys.vehicleChargingActive(teamId, siteParams), fetchWithError, {
    enabled: Boolean(teamId),
    refetchInterval: ACTIVE_REFRESH_MS,
    select: (r) => r?.data ?? [],
  });
  const rows = data || [];
  // Same rule as the Chargers card's In Use tile, so the counts match.
  const chargers = new Set(rows.map((r) => r.chargerId)).size;
  const vehicles = new Set(rows.filter((r) => r.vehicleId != null).map((r) => r.vehicleId)).size;
  const unknown = rows.filter((r) => r.vehicleId == null).length;

  return (
    <CollapsibleCard
      title="Vehicle Charging"
      subtitle="Fleet readiness, vehicles charging now and completed sessions — energy, cost and state of charge"
      icon={<Icon icon="eva:car-outline" width={22} height={22} />}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
      >
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ minHeight: 36 }}>
          <Tab value="readiness" sx={TAB_SX} label="Readiness" />
          <Tab
            value="now"
            sx={TAB_SX}
            // Counted in chargers, like the Chargers card's In Use tile.
            label={`Charging Now${chargers ? ` · ${chargers} charger${chargers === 1 ? "" : "s"}` : ""}`}
          />
          <Tab value="history" sx={TAB_SX} label="History" />
        </Tabs>
        {tab === "history" ? (
          <TimePeriodSelector value={period} onChange={setPeriod} options={PERIOD_OPTIONS} />
        ) : tab === "readiness" ? (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Latest SoC per battery · refreshes every minute
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {rows.length} session{rows.length === 1 ? "" : "s"} · {vehicles} vehicle{vehicles === 1 ? "" : "s"}
            {unknown > 0 ? ` + ${unknown} unknown` : ""} · live, refreshes every 30s
          </Typography>
        )}
      </Stack>
      {tab === "readiness" && <VehicleReadinessPanel teamId={teamId} siteId={siteId} />}
      {tab === "now" && <ChargingNowPanel rows={rows} isLoading={isLoading} error={error} />}
      {tab === "history" && (
        <HistoryPanel key={`${period}-${siteId ?? "all"}`} teamId={teamId} period={period} siteId={siteId} />
      )}
    </CollapsibleCard>
  );
};
VehicleChargingV2.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default VehicleChargingV2;
